import { getIconByName } from '@/lib/icon-map';
import type { SharedData } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { ChevronDown } from 'lucide-react';
import React from 'react';

interface MenuItem {
    id: number;
    title: string;
    url: string;
    type: 'link' | 'route' | 'dropdown' | 'divider';
    target: string;
    icon?: string | null;
    css_classes?: string | null;
    children?: MenuItem[];
}

interface MenuRendererProps {
    location: string;
    className?: string;
    itemClassName?: string;
    dropdownClassName?: string;
    dividerClassName?: string;
    nestedMode?: 'popover' | 'inline';
    nestedItemClassName?: string;
    renderItem?: (item: MenuItem, defaultRender: () => React.ReactNode) => React.ReactNode;
}

export function MenuRenderer({
    location,
    className = '',
    itemClassName = '',
    dropdownClassName = '',
    dividerClassName = 'border-t my-2',
    nestedMode = 'popover',
    nestedItemClassName = '',
    renderItem,
}: MenuRendererProps) {
    const { menus } = usePage<SharedData>().props as SharedData & { menus?: Record<string, { items: MenuItem[] }> };
    const menu = menus?.[location];

    if (!menu || !menu.items || menu.items.length === 0) {
        return null;
    }

    const renderIcon = (iconName?: string | null) => {
        const Icon = getIconByName(iconName);
        return Icon ? <Icon className="h-4 w-4" /> : null;
    };

    const renderMenuItem = (item: MenuItem, depth: number = 0): React.ReactNode => {
        // Handle divider
        if (item.type === 'divider') {
            return <div key={item.id} className={dividerClassName} />;
        }

        // Handle dropdown
        if (item.type === 'dropdown' && item.children) {
            if (nestedMode === 'inline') {
                const parentLink =
                    item.url && item.url !== '#' ? (
                        item.url.startsWith('http') || item.target === '_blank' ? (
                            <a
                                href={item.url}
                                target={item.target}
                                rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                                className={`flex items-center gap-2 ${itemClassName} ${item.css_classes || ''}`}
                            >
                                {renderIcon(item.icon)}
                                <span>{item.title}</span>
                            </a>
                        ) : (
                            <Link href={item.url} className={`flex items-center gap-2 ${itemClassName} ${item.css_classes || ''}`}>
                                {renderIcon(item.icon)}
                                <span>{item.title}</span>
                            </Link>
                        )
                    ) : (
                        <span className={`flex items-center gap-2 ${itemClassName} ${item.css_classes || ''}`}>
                            {renderIcon(item.icon)}
                            <span>{item.title}</span>
                        </span>
                    );

                const parentNode = renderItem ? renderItem(item, () => parentLink) : parentLink;

                return (
                    <div key={item.id}>
                        {parentNode}
                        <div className="flex flex-col">
                            {item.children.map((child) => {
                                const childLink =
                                    child.url && child.url !== '#' ? (
                                        child.url.startsWith('http') || child.target === '_blank' ? (
                                            <a
                                                href={child.url}
                                                target={child.target}
                                                rel={child.target === '_blank' ? 'noopener noreferrer' : undefined}
                                                className={`${nestedItemClassName} ${child.css_classes || ''}`}
                                            >
                                                {child.title}
                                            </a>
                                        ) : (
                                            <Link href={child.url} className={`${nestedItemClassName} ${child.css_classes || ''}`}>
                                                {child.title}
                                            </Link>
                                        )
                                    ) : (
                                        <span className={`${nestedItemClassName} ${child.css_classes || ''}`}>{child.title}</span>
                                    );
                                return renderItem ? (
                                    <React.Fragment key={child.id}>{renderItem(child, () => childLink)}</React.Fragment>
                                ) : (
                                    <React.Fragment key={child.id}>{childLink}</React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            return (
                <div key={item.id} className={`group relative ${item.css_classes || ''}`}>
                    <button className={`flex items-center gap-2 ${itemClassName}`}>
                        {renderIcon(item.icon)}
                        <span>{item.title}</span>
                        <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                    </button>
                    <div
                        className={`absolute top-full left-0 hidden min-w-[200px] flex-col bg-background shadow-lg group-hover:flex ${dropdownClassName}`}
                    >
                        {item.children.map((child) => renderMenuItem(child, depth + 1))}
                    </div>
                </div>
            );
        }

        // Handle regular link/route
        const linkContent = (
            <>
                {renderIcon(item.icon)}
                <span>{item.title}</span>
            </>
        );

        const defaultRender = () => {
            if (item.url && item.url !== '#') {
                // External link
                if (item.url.startsWith('http') || item.target === '_blank') {
                    return (
                        <a
                            key={item.id}
                            href={item.url}
                            target={item.target}
                            rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                            className={`flex items-center gap-2 ${itemClassName} ${item.css_classes || ''}`}
                        >
                            {linkContent}
                        </a>
                    );
                }

                // Internal link
                return (
                    <Link key={item.id} href={item.url} className={`flex items-center gap-2 ${itemClassName} ${item.css_classes || ''}`}>
                        {linkContent}
                    </Link>
                );
            }

            // No URL (placeholder)
            return (
                <span key={item.id} className={`flex items-center gap-2 ${itemClassName} ${item.css_classes || ''}`}>
                    {linkContent}
                </span>
            );
        };

        // Allow custom rendering if provided
        if (renderItem) {
            return renderItem(item, defaultRender);
        }

        return defaultRender();
    };

    return <nav className={className}>{menu.items.map((item: MenuItem) => renderMenuItem(item))}</nav>;
}

// Specialized version for footer with column support
export function FooterMenuRenderer({
    location,
    className = '',
    linkClassName = 'text-cms-body text-muted-foreground hover:text-primary',
}: {
    location: string;
    className?: string;
    linkClassName?: string;
}) {
    const { menus } = usePage<SharedData>().props as SharedData & { menus?: Record<string, { items: MenuItem[] }> };
    const menu = menus?.[location];

    if (!menu || !menu.items || menu.items.length === 0) {
        return null;
    }

    return (
        <ul className={`space-y-2 ${className}`}>
            {menu.items.map((item: MenuItem) => (
                <li key={item.id}>
                    {item.url && item.url !== '#' ? (
                        item.url.startsWith('http') || item.target === '_blank' ? (
                            <a
                                href={item.url}
                                target={item.target}
                                rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                                className={`${linkClassName} ${item.css_classes || ''}`}
                            >
                                {item.title}
                            </a>
                        ) : (
                            <Link href={item.url} className={`${linkClassName} ${item.css_classes || ''}`}>
                                {item.title}
                            </Link>
                        )
                    ) : (
                        <span className={`text-cms-body text-muted-foreground ${item.css_classes || ''}`}>{item.title}</span>
                    )}
                </li>
            ))}
        </ul>
    );
}
